import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaArrowLeft,
    FaArrowRight,
    FaBuilding,
    FaCheck,
    FaCheckCircle,
    FaClock,
    FaExclamationTriangle,
    FaFileInvoiceDollar,
    FaIdCard,
    FaLock,
    FaMobileAlt,
    FaMoneyBillWave,
    FaReceipt,
    FaRedo,
    FaShieldAlt,
    FaTimesCircle,
    FaUniversity,
    FaUpload,
} from "react-icons/fa";
import api from "../api/api";

const BRAND = {
    navy: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    orange: "#C0750D",
    green: "#4FAE22",
    red: "#C62828",
    text: "#172033",
    muted: "#687386",
    border: "#DCE4EE",
    background: "#F4F7FB",
    white: "#FFFFFF",
};

const APPLICATION_NUMBER_KEY =
    "paf_entrance_application_number";

const APPLICATION_KEY =
    "paf_entrance_application";

const STATUS_ENDPOINT =
    "/api/entrance-applications/payment/status/";

const PAYMENT_ENDPOINT =
    "/api/entrance-applications/payment/";

const LOGIN_ROUTE = "/";

const RECEIPT_ROUTE =
    "/entranceReceipt";

const ENTRANCE_SUBJECTS_ROUTE =
    "/entranceSubjects";

const AMOUNT = 850;

const CURRENCY = "LRD";

const MAX_FILE_SIZE =
    5 * 1024 * 1024;

const ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "application/pdf",
];

const PAYMENT_METHODS = [
    {
        value: "mobile_money",
        label: "Mobile Money",
        description:
            "MTN or Orange Money",
        icon: FaMobileAlt,
    },
    {
        value: "bank",
        label: "Bank Deposit",
        description:
            "Deposit through a listed bank",
        icon: FaUniversity,
    },
    {
        value: "cash",
        label: "Cash",
        description:
            "Cash payment to the institution",
        icon: FaMoneyBillWave,
    },
    {
        value: "card",
        label: "Card / Online",
        description:
            "Card or online payment",
        icon: FaMoneyBillWave,
    },
];

const MOBILE_PROVIDERS = [
    {
        value: "lonestar_mtn",
        label:
            "Lonestar Cell MTN Mobile Money",
    },
    {
        value: "orange_money",
        label: "Orange Money",
    },
];

const BANKS = [
    {
        value: "lbdi",
        label:
            "Liberian Bank for Development & Investment (LBDI)",
    },
    {
        value: "ecobank",
        label: "Ecobank Liberia",
    },
    {
        value: "international_bank_liberia",
        label: "International Bank Liberia",
    },
    {
        value: "bloom_bank_africa",
        label: "Bloom Bank Africa Liberia",
    },
    {
        value: "sapelle",
        label:
            "Sapelle International Bank Liberia",
    },
    {
        value: "uba",
        label:
            "United Bank for Africa (UBA) Liberia",
    },
    {
        value: "access_bank",
        label: "Access Bank Liberia",
    },
    {
        value: "gt_bank",
        label:
            "Guaranty Trust Bank (GT Bank) Liberia",
    },
    {
        value: "afriland_first_bank",
        label:
            "Afriland First Bank Liberia",
    },
    {
        value: "first_atlantic_bank",
        label:
            "First Atlantic Bank Liberia",
    },
];

const normalizeApplicationNumber = (
    value
) =>
    String(value || "")
        .trim()
        .toUpperCase();

const readStoredApplication = () => {
    try {
        const stored =
            localStorage.getItem(
                APPLICATION_KEY
            );

        if (!stored) {
            return null;
        }

        const parsed =
            JSON.parse(stored);

        return (
            parsed &&
            typeof parsed === "object" &&
            !Array.isArray(parsed)
        )
            ? parsed
            : null;
    } catch (error) {
        console.error(
            "Unable to read stored application:",
            error
        );

        return null;
    }
};

const safeText = (
    value,
    fallback = "—"
) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    if (
        typeof value === "string" ||
        typeof value === "number"
    ) {
        return String(value);
    }

    if (
        typeof value === "object"
    ) {
        return (
            value.name ||
            value.label ||
            value.title ||
            value.display_name ||
            value.academic_year ||
            value.year ||
            value.value ||
            fallback
        );
    }

    return fallback;
};

const getApplicantName = (
    application
) => {
    if (!application) {
        return "Applicant";
    }

    if (application.applicant_name) {
        return safeText(
            application.applicant_name,
            "Applicant"
        );
    }

    const name = [
        application.first_name,
        application.last_name,
    ]
        .filter(Boolean)
        .join(" ")
        .trim();

    return name || "Applicant";
};

const formatMoney = (
    amount,
    currency = CURRENCY
) => {
    const number = Number(amount);

    if (!Number.isFinite(number)) {
        return `${currency} 0.00`;
    }

    return `${currency} ${number.toLocaleString(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )}`;
};

const formatDate = (
    value
) => {
    if (!value) {
        return "—";
    }

    const date = new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "—";
    }

    return date.toLocaleString(
        "en-US",
        {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        }
    );
};

const getErrorMessage = (
    error,
    fallback
) => {
    const data =
        error?.response?.data;

    if (data instanceof Blob) {
        return fallback;
    }

    if (typeof data === "string") {
        return data;
    }

    if (data?.message) {
        return safeText(
            data.message,
            fallback
        );
    }

    if (data?.detail) {
        return safeText(
            data.detail,
            fallback
        );
    }

    if (
        data?.errors
            ?.application_number?.[0]
    ) {
        return safeText(
            data.errors
                .application_number[0],
            fallback
        );
    }

    return fallback;
};

const EntranceRegistrationPage = () => {
    const navigate = useNavigate();

    const storedApplication = useMemo(
        () => readStoredApplication(),
        []
    );

    const initialApplicationNumber =
        normalizeApplicationNumber(
            localStorage.getItem(
                APPLICATION_NUMBER_KEY
            ) ||
                storedApplication
                    ?.application_number
        );

    const [
        application,
        setApplication,
    ] = useState(
        storedApplication
    );

    const [
        applicationNumber,
    ] = useState(
        initialApplicationNumber
    );

    const [
        payment,
        setPayment,
    ] = useState(null);

    const [
        paymentStatus,
        setPaymentStatus,
    ] = useState("checking");

    const [
        statusError,
        setStatusError,
    ] = useState("");

    const [
        checkingStatus,
        setCheckingStatus,
    ] = useState(false);

    const [
        paymentMethod,
        setPaymentMethod,
    ] = useState("");

    const [
        mobileMoneyProvider,
        setMobileMoneyProvider,
    ] = useState("");

    const [
        bank,
        setBank,
    ] = useState("");

    const [
        transactionReference,
        setTransactionReference,
    ] = useState("");

    const [
        paymentProof,
        setPaymentProof,
    ] = useState(null);

    const [
        submitting,
        setSubmitting,
    ] = useState(false);

    const showAlert = useCallback(
        ({
            icon,
            title,
            text,
            confirmButtonText = "OK",
            confirmButtonColor =
                BRAND.navy,
        }) =>
            Swal.fire({
                icon,
                title,
                text,
                confirmButtonText,
                confirmButtonColor,
                background:
                    BRAND.white,
                color: BRAND.text,
                allowOutsideClick:
                    false,
                customClass: {
                    popup:
                        "paf-swal-popup",
                    title:
                        "paf-swal-title",
                    confirmButton:
                        "paf-swal-button",
                },
            }),
        []
    );

    const checkPaymentStatus =
        useCallback(
            async ({
                silent = false,
            } = {}) => {
                if (!applicationNumber) {
                    return false;
                }

                if (!silent) {
                    setCheckingStatus(
                        true
                    );
                }

                setStatusError("");

                try {
                    const response =
                        await api.post(
                            STATUS_ENDPOINT,
                            {
                                application_number:
                                    applicationNumber,
                            }
                        );

                    const data =
                        response?.data ||
                        {};

                    if (
                        data.application
                    ) {
                        setApplication(
                            data.application
                        );

                        localStorage.setItem(
                            APPLICATION_KEY,
                            JSON.stringify(
                                data.application
                            )
                        );
                    }

                    const returnedPayment =
                        data.payment ||
                        null;

                    const paymentExists =
                        Boolean(
                            data.payment_exists
                        );

                    setPayment(
                        returnedPayment
                    );

                    if (
                        !paymentExists
                    ) {
                        setPaymentStatus(
                            "not_submitted"
                        );

                        return true;
                    }

                    const status =
                        String(
                            returnedPayment
                                ?.status ||
                                data.status ||
                                ""
                        )
                            .trim()
                            .toLowerCase();

                    if (
                        status ===
                            "verified" ||
                        data.is_verified ===
                            true
                    ) {
                        setPaymentStatus(
                            "verified"
                        );
                    } else if (
                        status ===
                            "rejected" ||
                        data.is_rejected ===
                            true
                    ) {
                        setPaymentStatus(
                            "rejected"
                        );
                    } else {
                        setPaymentStatus(
                            "submitted"
                        );
                    }

                    return true;
                } catch (error) {
                    console.error(
                        "Payment status check failed:",
                        error
                    );

                    setStatusError(
                        getErrorMessage(
                            error,
                            "We could not confirm your payment status right now."
                        )
                    );

                    return false;
                } finally {
                    if (!silent) {
                        setCheckingStatus(
                            false
                        );
                    }
                }
            },
            [applicationNumber]
        );

    useEffect(() => {
        if (!applicationNumber) {
            navigate(
                LOGIN_ROUTE,
                {
                    replace: true,
                }
            );

            return;
        }

        checkPaymentStatus();
    }, [
        applicationNumber,
        navigate,
        checkPaymentStatus,
    ]);

    useEffect(() => {
        if (
            !applicationNumber ||
            ![
                "submitted",
                "pending",
            ].includes(
                paymentStatus
            )
        ) {
            return undefined;
        }

        const interval =
            window.setInterval(
                () => {
                    checkPaymentStatus({
                        silent: true,
                    });
                },
                12000
            );

        return () =>
            window.clearInterval(
                interval
            );
    }, [
        applicationNumber,
        paymentStatus,
        checkPaymentStatus,
    ]);

    const handlePaymentMethodChange =
        (value) => {
            setPaymentMethod(
                value
            );

            if (
                value !==
                "mobile_money"
            ) {
                setMobileMoneyProvider(
                    ""
                );
            }

            if (value !== "bank") {
                setBank("");
            }
        };

    const handleFileChange = (
        event
    ) => {
        const file =
            event.target.files?.[0];

        if (!file) {
            setPaymentProof(null);
            return;
        }

        if (
            !ALLOWED_FILE_TYPES.includes(
                file.type
            )
        ) {
            event.target.value = "";
            setPaymentProof(null);

            showAlert({
                icon: "error",
                title:
                    "Invalid File Type",
                text:
                    "Please upload a JPG, JPEG, PNG, or PDF payment proof.",
                confirmButtonText:
                    "Choose Another File",
            });

            return;
        }

        if (
            file.size >
            MAX_FILE_SIZE
        ) {
            event.target.value = "";
            setPaymentProof(null);

            showAlert({
                icon: "warning",
                title:
                    "File Too Large",
                text:
                    "Payment proof must not be larger than 5 MB.",
                confirmButtonText:
                    "Choose Another File",
            });

            return;
        }

        setPaymentProof(file);
    };

const validatePaymentForm = () => {
    if (!paymentMethod) {
        return "Please select your payment method.";
    }

    if (
        paymentMethod === "mobile_money" &&
        !mobileMoneyProvider
    ) {
        return "Please select your mobile money provider.";
    }

    if (
        paymentMethod === "bank" &&
        !bank
    ) {
        return "Please select the bank used for payment.";
    }

    if (!transactionReference.trim()) {
        return "Please enter your transaction or payment reference.";
    }

    // Payment proof is OPTIONAL.
    // Do not return a validation error when no file is selected.

    return "";
};

const handleSubmitPayment = async (event) => {
    event.preventDefault();

    if (paymentStatus !== "not_submitted") {
        await checkPaymentStatus();
        return;
    }

    const validationError = validatePaymentForm();

    if (validationError) {
        await showAlert({
            icon: "warning",
            title: "Payment Information Incomplete",
            text: validationError,
            confirmButtonText: "Complete Information",
        });

        return;
    }

    // Confirm submission without payment proof.
    if (!paymentProof) {
        const result = await Swal.fire({
            icon: "warning",
            title: "Payment Proof Missing",
            html: `
                <p>
                    You did not submit
                    <strong>Payment Proof</strong>.
                </p>
                <p>
                    Do you want to continue submitting
                    your payment without it?
                </p>
            `,
            showCancelButton: true,
            confirmButtonText: "Yes, Continue",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND.green,
            cancelButtonColor: BRAND.red,
            reverseButtons: true,
            allowOutsideClick: false,
            allowEscapeKey: true,
        });

        if (!result.isConfirmed) {
            return;
        }
    }

    try {
        setSubmitting(true);

        const formData = new FormData();

        formData.append(
            "application_number",
            applicationNumber
        );

        formData.append(
            "payment_type",
            paymentMethod
        );

        formData.append(
            "amount",
            String(AMOUNT)
        );

        formData.append(
            "currency",
            CURRENCY
        );

        formData.append(
            "transaction_reference",
            transactionReference.trim()
        );

        if (paymentMethod === "mobile_money") {
            formData.append(
                "mobile_money_provider",
                mobileMoneyProvider
            );
        }

        if (paymentMethod === "bank") {
            formData.append(
                "bank",
                bank
            );
        }

        // Attach payment proof only when provided.
        if (paymentProof) {
            formData.append(
                "payment_proof",
                paymentProof
            );
        }

        const response = await api.post(
            PAYMENT_ENDPOINT,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );

        const returnedPayment =
            response?.data?.payment ||
            response?.data?.data ||
            null;

        if (returnedPayment) {
            setPayment(returnedPayment);
        }

        setPaymentStatus("submitted");

        // Reset the payment form.
        setPaymentProof(null);
        setTransactionReference("");
        setPaymentMethod("");
        setMobileMoneyProvider("");
        setBank("");

        await showAlert({
            icon: "success",
            title: "Payment Submitted",
            text:
                "Your entrance payment has been submitted successfully and is now awaiting verification.",
            confirmButtonText: "Continue",
            confirmButtonColor: BRAND.green,
        });

        await checkPaymentStatus({
            silent: true,
        });
    } catch (error) {
        console.error(
            "Entrance payment submission failed:",
            error
        );

        const existingPayment =
            error?.response?.data?.payment;

        if (
            existingPayment ||
            error?.response?.status === 409
        ) {
            if (existingPayment) {
                setPayment(existingPayment);
            }

            await checkPaymentStatus({
                silent: true,
            });

            await showAlert({
                icon: "info",
                title: "Payment Already Exists",
                text:
                    "A payment record already exists for this application. You cannot submit another payment.",
                confirmButtonText: "View Status",
                confirmButtonColor: BRAND.blue,
            });

            return;
        }

        await showAlert({
            icon: "error",
            title: "Payment Submission Failed",
            text: getErrorMessage(
                error,
                "We could not submit your payment. Please try again."
            ),
            confirmButtonText: "Try Again",
            confirmButtonColor: BRAND.red,
        });
    } finally {
        setSubmitting(false);
    }
};

    const handleOpenReceipt =
        () => {
            if (
                paymentStatus !==
                "verified"
            ) {
                showAlert({
                    icon: "warning",
                    title:
                        "Receipt Not Available",
                    text:
                        "Your entrance payment must be verified before the official receipt can be viewed.",
                });

                return;
            }

            const receiptData = {
                application,
                payment,
                applicationNumber,
                amount:
                    payment?.amount ||
                    AMOUNT,
                currency:
                    payment?.currency ||
                    CURRENCY,
            };

            try {
                localStorage.setItem(
                    "paf_entrance_receipt",
                    JSON.stringify(
                        receiptData
                    )
                );
            } catch (error) {
                console.error(
                    "Unable to save receipt data:",
                    error
                );
            }

            navigate(
                RECEIPT_ROUTE,
                {
                    state:
                        receiptData,
                }
            );
        };

    /*
     * ---------------------------------------------------------
     * START ENTRANCE EXAMINATION
     * ---------------------------------------------------------
     *
     * The important fix is that we now pass the complete
     * application context to EntranceSubjects.
     *
     * EntranceSubjects can therefore access:
     *
     * location.state.application
     * location.state.applicationNumber
     *
     * The application number is also still stored in
     * localStorage, so the flow remains compatible with
     * your existing EntranceSubjects page.
     * ---------------------------------------------------------
     */
    const handleOpenEntranceSubjects =
        () => {
            if (
                paymentStatus !==
                "verified"
            ) {
                showAlert({
                    icon: "warning",
                    title:
                        "Entrance Examination Locked",
                    text:
                        "Your entrance payment must be verified before you can access the entrance examination.",
                    confirmButtonText:
                        "OK",
                    confirmButtonColor:
                        BRAND.orange,
                });

                return;
            }

            if (
                !applicationNumber
            ) {
                showAlert({
                    icon: "error",
                    title:
                        "Application Not Found",
                    text:
                        "Your application number could not be identified. Please return to the login page and try again.",
                    confirmButtonText:
                        "OK",
                    confirmButtonColor:
                        BRAND.red,
                });

                return;
            }

            /*
             * Keep the current application synchronized
             * before opening the examination subjects.
             */
            try {
                if (application) {
                    localStorage.setItem(
                        APPLICATION_KEY,
                        JSON.stringify(
                            application
                        )
                    );
                }

                localStorage.setItem(
                    APPLICATION_NUMBER_KEY,
                    applicationNumber
                );
            } catch (error) {
                console.error(
                    "Unable to preserve entrance application data:",
                    error
                );
            }

            /*
             * IMPORTANT:
             * Pass application information to EntranceSubjects.
             */
            navigate(
                ENTRANCE_SUBJECTS_ROUTE,
                {
                    state: {
                        application,
                        applicationNumber,
                    },
                }
            );
        };

    const applicantName =
        getApplicantName(
            application
        );

    const academicYear =
        safeText(
            application?.academic_year
        );

    const isFormVisible =
        paymentStatus ===
        "not_submitted";

    const isPending =
        paymentStatus ===
            "submitted" ||
        paymentStatus ===
            "pending";

    const isVerified =
        paymentStatus ===
        "verified";

    const isRejected =
        paymentStatus ===
        "rejected";

    if (!applicationNumber) {
        return null;
    }

    return (
        <div className="paf-page">
            <style>{`
                * {
                    box-sizing: border-box;
                }

                @keyframes pafSpin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                @keyframes pafPulse {
                    0%, 100% {
                        box-shadow:
                            0 0 0 0 rgba(79,174,34,.12);
                    }

                    50% {
                        box-shadow:
                            0 0 0 12px rgba(79,174,34,0);
                    }
                }

                .paf-page {
                    min-height: 100vh;
                    background:
                        linear-gradient(
                            180deg,
                            #F5F8FC 0%,
                            #FFFFFF 55%,
                            #F3F7FB 100%
                        );
                    color: ${BRAND.text};
                    padding: 24px 18px 50px;
                }

                .paf-container {
                    width: 100%;
                    max-width: 1180px;
                    margin: 0 auto;
                }

                .paf-header {
                    position: relative;
                    overflow: hidden;
                    background:
                        linear-gradient(
                            120deg,
                            ${BRAND.navy},
                            ${BRAND.blue}
                        );
                    border-radius: 18px;
                    padding: 26px 28px;
                    color: #FFFFFF;
                    margin-bottom: 18px;
                    box-shadow:
                        0 15px 38px
                        rgba(6,42,75,.15);
                }

                .paf-header-content {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                }

                .paf-brand {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    min-width: 0;
                }

                .paf-brand-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 13px;
                    background:
                        rgba(255,255,255,.1);
                    border: 1px solid
                        rgba(255,255,255,.18);
                    color: ${BRAND.gold};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 21px;
                    flex-shrink: 0;
                }

                .paf-eyebrow {
                    margin: 0 0 4px;
                    color: ${BRAND.gold};
                    font-size: 10px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: 1.5px;
                }

                .paf-title {
                    margin: 0;
                    font-size: 25px;
                    line-height: 1.2;
                    font-weight: 900;
                }

                .paf-subtitle {
                    margin: 6px 0 0;
                    color:
                        rgba(255,255,255,.72);
                    font-size: 12px;
                }

                .paf-back {
                    border: 1px solid
                        rgba(255,255,255,.2);
                    background:
                        rgba(255,255,255,.08);
                    color: #FFFFFF;
                    border-radius: 9px;
                    padding: 10px 14px;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 11px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: .2s ease;
                }

                .paf-back:hover {
                    background: #FFFFFF;
                    color: ${BRAND.navy};
                }

                .paf-steps {
                    display: grid;
                    grid-template-columns:
                        repeat(4, 1fr);
                    gap: 8px;
                    margin-bottom: 18px;
                }

                .paf-step {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    padding: 10px 12px;
                    background: #FFFFFF;
                    border: 1px solid
                        ${BRAND.border};
                    border-radius: 10px;
                    color: #8490A0;
                    font-size: 10px;
                    font-weight: 800;
                }

                .paf-step-number {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #EEF2F6;
                    color: #7A8494;
                    font-size: 9px;
                    font-weight: 900;
                    flex-shrink: 0;
                }

                .paf-step.active {
                    color: ${BRAND.navy};
                    border-color: #B8C9DA;
                }

                .paf-step.active
                .paf-step-number {
                    background: ${BRAND.navy};
                    color: #FFFFFF;
                }

                .paf-step.complete {
                    color: ${BRAND.green};
                    border-color: #D9EAD4;
                }

                .paf-step.complete
                .paf-step-number {
                    background: #EAF7E5;
                    color: ${BRAND.green};
                }

                .paf-application,
                .paf-card {
                    background: #FFFFFF;
                    border: 1px solid
                        ${BRAND.border};
                    border-radius: 15px;
                    box-shadow:
                        0 10px 30px
                        rgba(6,42,75,.055);
                }

                .paf-application {
                    padding: 17px;
                    margin-bottom: 18px;
                }

                .paf-application-head {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 15px;
                    margin-bottom: 13px;
                }

                .paf-section-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin: 0;
                    color: ${BRAND.navy};
                    font-size: 14px;
                    font-weight: 900;
                }

                .paf-section-title svg {
                    color: ${BRAND.blue};
                }

                .paf-app-number {
                    background: #F1F5F9;
                    border: 1px solid #E1E8F0;
                    color: ${BRAND.navy};
                    padding: 7px 10px;
                    border-radius: 7px;
                    font-size: 10px;
                    font-weight: 900;
                }

                .paf-app-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(3, 1fr);
                    gap: 8px;
                }

                .paf-app-item {
                    background: #F8FAFC;
                    border: 1px solid #EEF2F6;
                    border-radius: 9px;
                    padding: 11px;
                    min-width: 0;
                }

                .paf-label {
                    display: block;
                    margin-bottom: 5px;
                    color: #7B8796;
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: .7px;
                }

                .paf-value {
                    color: ${BRAND.navy};
                    font-size: 12px;
                    font-weight: 800;
                    line-height: 1.4;
                    overflow-wrap: anywhere;
                }

                .paf-layout {
                    display: grid;
                    grid-template-columns:
                        minmax(0, 1.55fr)
                        minmax(280px, .75fr);
                    gap: 18px;
                    align-items: start;
                }

                .paf-card {
                    overflow: hidden;
                }

                .paf-card-body {
                    padding: 23px;
                }

                .paf-card-head {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 18px;
                    padding-bottom: 18px;
                    margin-bottom: 19px;
                    border-bottom: 1px solid #EDF1F5;
                }

                .paf-card-title {
                    margin: 0;
                    color: ${BRAND.navy};
                    font-size: 19px;
                    font-weight: 900;
                }

                .paf-card-text {
                    max-width: 550px;
                    margin: 5px 0 0;
                    color: ${BRAND.muted};
                    font-size: 11px;
                    line-height: 1.65;
                }

                .paf-amount {
                    min-width: 125px;
                    padding: 10px 13px;
                    border-left: 3px solid
                        ${BRAND.gold};
                    background: #FFFDF2;
                }

                .paf-amount-label {
                    display: block;
                    color: #8A6A00;
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                }

                .paf-amount-value {
                    display: block;
                    margin-top: 2px;
                    color: ${BRAND.navy};
                    font-size: 16px;
                    font-weight: 900;
                }

                .paf-method-title {
                    margin-bottom: 9px;
                    color: ${BRAND.text};
                    font-size: 11px;
                    font-weight: 900;
                }

                .paf-method-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(2, 1fr);
                    gap: 8px;
                    margin-bottom: 18px;
                }

                .paf-method {
                    border: 1px solid
                        ${BRAND.border};
                    border-radius: 9px;
                    padding: 12px;
                    background: #FFFFFF;
                    cursor: pointer;
                    transition: .2s ease;
                }

                .paf-method:hover {
                    border-color: #A9B9CB;
                    background: #FBFCFD;
                }

                .paf-method.selected {
                    border-color: ${BRAND.blue};
                    background: #F4F8FC;
                    box-shadow:
                        inset 3px 0 0
                        ${BRAND.blue};
                }

                .paf-method-top {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .paf-method-icon {
                    color: ${BRAND.blue};
                    font-size: 16px;
                }

                .paf-method-name {
                    color: ${BRAND.navy};
                    font-size: 11px;
                    font-weight: 900;
                }

                .paf-method-check {
                    margin-left: auto;
                    color: ${BRAND.green};
                }

                .paf-method-desc {
                    margin-top: 5px;
                    color: ${BRAND.muted};
                    font-size: 9px;
                    line-height: 1.45;
                }

                .paf-field {
                    margin-bottom: 14px;
                }

                .paf-input,
                .paf-select {
                    width: 100%;
                    border: 1px solid
                        ${BRAND.border};
                    border-radius: 8px;
                    padding: 11px 12px;
                    background: #FCFDFE;
                    color: ${BRAND.text};
                    font-size: 12px;
                }

                .paf-input:focus,
                .paf-select:focus {
                    outline: none;
                    border-color: ${BRAND.blue};
                    box-shadow:
                        0 0 0 3px
                        rgba(6,58,107,.07);
                }

                .paf-upload {
                    display: block;
                    padding: 16px;
                    border: 1px dashed #B9C7D5;
                    border-radius: 9px;
                    background: #FAFCFE;
                    text-align: center;
                    cursor: pointer;
                }

                .paf-upload-icon {
                    color: ${BRAND.blue};
                    font-size: 20px;
                    margin-bottom: 6px;
                }

                .paf-upload-name {
                    color: ${BRAND.navy};
                    font-size: 11px;
                    font-weight: 800;
                    overflow-wrap: anywhere;
                }

                .paf-upload-help {
                    margin-top: 4px;
                    color: ${BRAND.muted};
                    font-size: 9px;
                }

                .paf-security {
                    display: flex;
                    align-items: flex-start;
                    gap: 7px;
                    padding: 11px 12px;
                    border-radius: 8px;
                    background: #F6F8FB;
                    color: ${BRAND.muted};
                    font-size: 9px;
                    line-height: 1.55;
                }

                .paf-security svg {
                    color: ${BRAND.green};
                    flex-shrink: 0;
                    margin-top: 1px;
                }

                .paf-submit {
                    width: 100%;
                    border: none;
                    border-radius: 8px;
                    padding: 13px;
                    background:
                        linear-gradient(
                            135deg,
                            ${BRAND.navy},
                            ${BRAND.blue}
                        );
                    color: #FFFFFF;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-size: 12px;
                    font-weight: 900;
                    cursor: pointer;
                }

                .paf-submit:disabled,
                .paf-receipt-button:disabled {
                    opacity: .65;
                    cursor: not-allowed;
                }

                .paf-side {
                    padding: 20px;
                }

                .paf-side-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding-bottom: 14px;
                    margin-bottom: 4px;
                    border-bottom: 1px solid #EDF1F5;
                }

                .paf-side-icon {
                    width: 37px;
                    height: 37px;
                    border-radius: 9px;
                    background: #F0F5FA;
                    color: ${BRAND.blue};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .paf-side-title {
                    margin: 0;
                    color: ${BRAND.navy};
                    font-size: 14px;
                    font-weight: 900;
                }

                .paf-side-subtitle {
                    margin-top: 2px;
                    color: ${BRAND.muted};
                    font-size: 9px;
                }

                .paf-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 10px 0;
                    border-bottom: 1px solid #EDF1F5;
                }

                .paf-row:last-child {
                    border-bottom: none;
                }

                .paf-row-label {
                    color: #7B8796;
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                }

                .paf-row-value {
                    max-width: 62%;
                    color: ${BRAND.navy};
                    font-size: 10px;
                    font-weight: 800;
                    text-align: right;
                    line-height: 1.45;
                    overflow-wrap: anywhere;
                }

                .paf-checking {
                    padding: 70px 20px;
                    text-align: center;
                }

                .paf-spinner {
                    width: 38px;
                    height: 38px;
                    margin: 0 auto 15px;
                    border: 3px solid #E7EDF4;
                    border-top-color: ${BRAND.blue};
                    border-radius: 50%;
                    animation:
                        pafSpin .75s linear infinite;
                }

                .paf-status {
                    padding: 36px 28px;
                    text-align: center;
                }

                .paf-status-icon {
                    width: 64px;
                    height: 64px;
                    margin: 0 auto 15px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 26px;
                }

                .paf-status-icon.pending {
                    background: #FFF6DD;
                    color: ${BRAND.orange};
                }

                .paf-status-icon.verified {
                    background: #EAF7E5;
                    color: ${BRAND.green};
                    animation:
                        pafPulse 2s infinite;
                }

                .paf-status-icon.rejected {
                    background: #FDECEC;
                    color: ${BRAND.red};
                }

                .paf-status-title {
                    margin: 0;
                    color: ${BRAND.navy};
                    font-size: 23px;
                    font-weight: 900;
                }

                .paf-status-title.verified {
                    color: ${BRAND.green};
                }

                .paf-status-title.rejected {
                    color: ${BRAND.red};
                }

                .paf-status-text {
                    max-width: 570px;
                    margin: 8px auto 0;
                    color: ${BRAND.muted};
                    font-size: 11px;
                    line-height: 1.7;
                }

                .paf-record {
                    margin-top: 20px;
                    padding: 14px;
                    border: 1px solid #E8EEF4;
                    border-radius: 10px;
                    background: #FAFBFC;
                    text-align: left;
                }

                .paf-record-title {
                    margin: 0 0 5px;
                    color: ${BRAND.navy};
                    font-size: 11px;
                    font-weight: 900;
                }

                .paf-receipt-box {
                    max-width: 600px;
                    margin: 20px auto 0;
                    padding: 20px;
                    border: 1px solid #D8E4EF;
                    border-left: 4px solid
                        ${BRAND.gold};
                    border-radius: 11px;
                    background:
                        linear-gradient(
                            180deg,
                            #FFFDF5,
                            #FFFFFF
                        );
                    text-align: left;
                }

                .paf-receipt-heading {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    color: ${BRAND.navy};
                    font-size: 14px;
                    font-weight: 900;
                }

                .paf-receipt-heading svg {
                    color: ${BRAND.gold};
                    font-size: 18px;
                }

                .paf-receipt-text {
                    margin: 7px 0 14px;
                    color: ${BRAND.muted};
                    font-size: 10px;
                    line-height: 1.6;
                }

                .paf-receipt-button {
                    width: 100%;
                    border: none;
                    border-radius: 8px;
                    padding: 13px;
                    background:
                        linear-gradient(
                            135deg,
                            ${BRAND.navy},
                            ${BRAND.blue}
                        );
                    color: #FFFFFF;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-size: 11px;
                    font-weight: 900;
                    cursor: pointer;
                }

                .paf-error {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 11px 13px;
                    margin-bottom: 16px;
                    border: 1px solid #FED7AA;
                    border-radius: 9px;
                    background: #FFF7ED;
                    color: #9A3412;
                    font-size: 10px;
                    font-weight: 800;
                }

                .paf-error-message {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                }

                .paf-retry {
                    border: 1px solid #E7C89F;
                    border-radius: 7px;
                    background: #FFFFFF;
                    color: #9A3412;
                    padding: 7px 10px;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 9px;
                    font-weight: 900;
                    cursor: pointer;
                }

                .paf-rejected-note {
                    max-width: 580px;
                    margin: 18px auto 0;
                    padding: 13px;
                    border: 1px solid #FECACA;
                    border-radius: 8px;
                    background: #FEF2F2;
                    color: #991B1B;
                    text-align: left;
                    font-size: 10px;
                    line-height: 1.6;
                }

                .paf-footer {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 5px;
                    margin-top: 23px;
                    color: #8A94A3;
                    font-size: 9px;
                }

                .paf-footer svg {
                    color: ${BRAND.green};
                }

                .paf-swal-popup {
                    border-radius: 14px !important;
                }

                .paf-swal-title {
                    font-size: 20px !important;
                }

                .paf-swal-button {
                    border-radius: 8px !important;
                    padding: 10px 18px !important;
                    font-weight: 800 !important;
                }

                @media (max-width: 900px) {
                    .paf-layout {
                        grid-template-columns: 1fr;
                    }

                    .paf-app-grid {
                        grid-template-columns:
                            repeat(2, 1fr);
                    }
                }

                @media (max-width: 640px) {
                    .paf-page {
                        padding:
                            12px 10px 35px;
                    }

                    .paf-header {
                        padding:
                            20px 17px;
                        border-radius: 13px;
                    }

                    .paf-header-content {
                        align-items:
                            flex-start;
                    }

                    .paf-title {
                        font-size: 20px;
                    }

                    .paf-subtitle {
                        font-size: 10px;
                    }

                    .paf-brand-icon {
                        width: 42px;
                        height: 42px;
                    }

                    .paf-back {
                        padding:
                            8px 9px;
                    }

                    .paf-back span {
                        display: none;
                    }

                    .paf-steps {
                        grid-template-columns:
                            repeat(2, 1fr);
                    }

                    .paf-app-grid,
                    .paf-method-grid {
                        grid-template-columns:
                            1fr;
                    }

                    .paf-application-head {
                        align-items:
                            flex-start;
                        flex-direction:
                            column;
                    }

                    .paf-card-body,
                    .paf-side {
                        padding: 17px;
                    }

                    .paf-card-head {
                        flex-direction:
                            column;
                    }

                    .paf-amount {
                        width: 100%;
                    }

                    .paf-status {
                        padding:
                            30px 17px;
                    }
                }

                @media (max-width: 520px) {
                    .paf-receipt-box > div {
                        grid-template-columns:
                            1fr !important;
                    }
                }
            `}</style>

            <div className="paf-container">
                <header className="paf-header">
                    <div className="paf-header-content">
                        <div className="paf-brand">
                            <div className="paf-brand-icon">
                                <FaBuilding />
                            </div>

                            <div>
                                <div className="paf-eyebrow">
                                    Passion Aids the Future
                                </div>

                                <h1 className="paf-title">
                                    Entrance Registration
                                </h1>

                                <p className="paf-subtitle">
                                    Application payment and
                                    verification portal
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="paf-back"
                            onClick={() =>
                                navigate(
                                    LOGIN_ROUTE
                                )
                            }
                        >
                            <FaArrowLeft />

                            <span>
                                Back to Login
                            </span>
                        </button>
                    </div>
                </header>

                <StepIndicator
                    status={
                        paymentStatus
                    }
                />

                <section className="paf-application">
                    <div className="paf-application-head">
                        <h2 className="paf-section-title">
                            <FaIdCard />
                            Application Information
                        </h2>

                        <div className="paf-app-number">
                            {
                                applicationNumber
                            }
                        </div>
                    </div>

                    <div className="paf-app-grid">
                        <InfoItem
                            label="Applicant"
                            value={
                                applicantName
                            }
                        />

                        <InfoItem
                            label="Program"
                            value={safeText(
                                application?.program
                            )}
                        />

                        <InfoItem
                            label="Academic Year"
                            value={
                                academicYear
                            }
                        />
                    </div>
                </section>

                {statusError && (
                    <motion.div
                        className="paf-error"
                        initial={{
                            opacity: 0,
                            y: -5,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                    >
                        <div className="paf-error-message">
                            <FaExclamationTriangle />

                            <span>
                                {
                                    statusError
                                }
                            </span>
                        </div>

                        <button
                            type="button"
                            className="paf-retry"
                            onClick={() =>
                                checkPaymentStatus()
                            }
                            disabled={
                                checkingStatus
                            }
                        >
                            <FaRedo />

                            {checkingStatus
                                ? "Checking..."
                                : "Retry"}
                        </button>
                    </motion.div>
                )}

                <AnimatePresence mode="wait">
                    {paymentStatus ===
                        "checking" && (
                        <motion.section
                            key="checking"
                            className="paf-card"
                            initial={{
                                opacity: 0,
                                y: 8,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            exit={{
                                opacity: 0,
                            }}
                        >
                            <div className="paf-checking">
                                <div className="paf-spinner" />

                                <h2
                                    style={{
                                        margin:
                                            "0 0 6px",
                                        color:
                                            BRAND.navy,
                                        fontSize:
                                            17,
                                        fontWeight:
                                            900,
                                    }}
                                >
                                    Checking Payment Status
                                </h2>

                                <p
                                    style={{
                                        margin: 0,
                                        color:
                                            BRAND.muted,
                                        fontSize:
                                            10,
                                    }}
                                >
                                    Please wait while we
                                    retrieve your payment
                                    record.
                                </p>
                            </div>
                        </motion.section>
                    )}

                    {isFormVisible && (
                        <motion.div
                            key="payment-form"
                            className="paf-layout"
                            initial={{
                                opacity: 0,
                                y: 8,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            exit={{
                                opacity: 0,
                                y: -8,
                            }}
                        >
                            <section className="paf-card">
                                <div className="paf-card-body">
                                    <div className="paf-card-head">
                                        <div>
                                            <h2 className="paf-card-title">
                                                Entrance Fee Payment
                                            </h2>

                                            <p className="paf-card-text">
                                                Submit the payment
                                                information used
                                                for this
                                                application. The
                                                payment will be
                                                reviewed before
                                                verification.
                                            </p>
                                        </div>

                                        <div className="paf-amount">
                                            <span className="paf-amount-label">
                                                Entrance Fee
                                            </span>

                                            <span className="paf-amount-value">
                                                {formatMoney(
                                                    AMOUNT
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <form
                                        onSubmit={
                                            handleSubmitPayment
                                        }
                                    >
                                        <div className="paf-method-title">
                                            Select Payment Method
                                        </div>

                                        <div className="paf-method-grid">
                                            {PAYMENT_METHODS.map(
                                                (
                                                    method
                                                ) => {
                                                    const Icon =
                                                        method.icon;

                                                    const selected =
                                                        paymentMethod ===
                                                        method.value;

                                                    return (
                                                        <div
                                                            key={
                                                                method.value
                                                            }
                                                            className={`paf-method ${
                                                                selected
                                                                    ? "selected"
                                                                    : ""
                                                            }`}
                                                            role="button"
                                                            tabIndex={
                                                                0
                                                            }
                                                            aria-pressed={
                                                                selected
                                                            }
                                                            onClick={() =>
                                                                handlePaymentMethodChange(
                                                                    method.value
                                                                )
                                                            }
                                                            onKeyDown={(
                                                                event
                                                            ) => {
                                                                if (
                                                                    event.key ===
                                                                        "Enter" ||
                                                                    event.key ===
                                                                        " "
                                                                ) {
                                                                    event.preventDefault();

                                                                    handlePaymentMethodChange(
                                                                        method.value
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <div className="paf-method-top">
                                                                <Icon className="paf-method-icon" />

                                                                <span className="paf-method-name">
                                                                    {
                                                                        method.label
                                                                    }
                                                                </span>

                                                                {selected && (
                                                                    <FaCheck className="paf-method-check" />
                                                                )}
                                                            </div>

                                                            <div className="paf-method-desc">
                                                                {
                                                                    method.description
                                                                }
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>

                                        {paymentMethod ===
                                            "mobile_money" && (
                                            <Field
                                                label="Mobile Money Provider"
                                            >
                                                <select
                                                    className="paf-select"
                                                    value={
                                                        mobileMoneyProvider
                                                    }
                                                    onChange={(
                                                        event
                                                    ) =>
                                                        setMobileMoneyProvider(
                                                            event
                                                                .target
                                                                .value
                                                        )
                                                    }
                                                >
                                                    <option value="">
                                                        Select provider
                                                    </option>

                                                    {MOBILE_PROVIDERS.map(
                                                        (
                                                            provider
                                                        ) => (
                                                            <option
                                                                key={
                                                                    provider.value
                                                                }
                                                                value={
                                                                    provider.value
                                                                }
                                                            >
                                                                {
                                                                    provider.label
                                                                }
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                            </Field>
                                        )}

                                        {paymentMethod ===
                                            "bank" && (
                                            <Field label="Bank">
                                                <select
                                                    className="paf-select"
                                                    value={
                                                        bank
                                                    }
                                                    onChange={(
                                                        event
                                                    ) =>
                                                        setBank(
                                                            event
                                                                .target
                                                                .value
                                                        )
                                                    }
                                                >
                                                    <option value="">
                                                        Select bank
                                                    </option>

                                                    {BANKS.map(
                                                        (
                                                            item
                                                        ) => (
                                                            <option
                                                                key={
                                                                    item.value
                                                                }
                                                                value={
                                                                    item.value
                                                                }
                                                            >
                                                                {
                                                                    item.label
                                                                }
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                            </Field>
                                        )}

                                        <Field
                                            label="Transaction / Payment Reference"
                                        >
                                            <input
                                                type="text"
                                                className="paf-input"
                                                value={
                                                    transactionReference
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setTransactionReference(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                placeholder="Enter transaction reference"
                                                maxLength={
                                                    150
                                                }
                                                autoComplete="off"
                                            />
                                        </Field>

                                         <Field label="Payment Proof (Optional)">
    <label className="paf-upload">
        <input
            type="file"
            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
            onChange={handleFileChange}
            style={{
                display: "none",
            }}
        />

        <FaUpload className="paf-upload-icon" />

        <div className="paf-upload-name">
            {paymentProof
                ? paymentProof.name
                : "Click to upload payment proof (optional)"}
        </div>

        <div className="paf-upload-help">
            JPG, JPEG, PNG or PDF · Maximum 5 MB
        </div>
    </label>
</Field>

                                        <div className="paf-security">
                                            <FaShieldAlt />

                                            <span>
                                                Your payment is
                                                linked directly
                                                to application{" "}
                                                <strong>
                                                    {
                                                        applicationNumber
                                                    }
                                                </strong>
                                                .
                                            </span>
                                        </div>

                                        <button
                                            type="submit"
                                            className="paf-submit"
                                            disabled={
                                                submitting
                                            }
                                            style={{
                                                marginTop:
                                                    14,
                                            }}
                                        >
                                            {submitting ? (
                                                <>
                                                    <Spinner />
                                                    Submitting
                                                    Payment...
                                                </>
                                            ) : (
                                                <>
                                                    Submit Payment
                                                    <FaArrowRight />
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </section>

                            <aside className="paf-card paf-side">
                                <SideHeader
                                    icon={
                                        <FaFileInvoiceDollar />
                                    }
                                    title="Payment Summary"
                                    subtitle="Review before submission"
                                />

                                <SummaryRow
                                    label="Applicant"
                                    value={
                                        applicantName
                                    }
                                />

                                <SummaryRow
                                    label="Application"
                                    value={
                                        applicationNumber
                                    }
                                />

                                <SummaryRow
                                    label="Program"
                                    value={safeText(
                                        application?.program
                                    )}
                                />

                                <SummaryRow
                                    label="Academic Year"
                                    value={
                                        academicYear
                                    }
                                />

                                <SummaryRow
                                    label="Entrance Fee"
                                    value={formatMoney(
                                        AMOUNT
                                    )}
                                    highlight
                                />

                                <div
                                    className="paf-security"
                                    style={{
                                        marginTop:
                                            14,
                                    }}
                                >
                                    <FaLock />

                                    <span>
                                        Your application
                                        number securely
                                        connects your
                                        application with
                                        your payment.
                                    </span>
                                </div>
                            </aside>
                        </motion.div>
                    )}

                    {isPending && (
                        <motion.div
                            key="pending"
                            className="paf-layout"
                            initial={{
                                opacity: 0,
                                y: 8,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            exit={{
                                opacity: 0,
                            }}
                        >
                            <section className="paf-card">
                                <div className="paf-status">
                                    <div className="paf-status-icon pending">
                                        <FaClock />
                                    </div>

                                    <h2 className="paf-status-title">
                                        Payment Submitted
                                    </h2>

                                    <p className="paf-status-text">
                                        Your payment has been
                                        received and is
                                        currently awaiting
                                        administrative
                                        verification.
                                    </p>

                                    <PaymentRecord
                                        payment={
                                            payment
                                        }
                                        amount={
                                            AMOUNT
                                        }
                                        currency={
                                            CURRENCY
                                        }
                                    />

                                    <div
                                        className="paf-security"
                                        style={{
                                            marginTop:
                                                17,
                                            textAlign:
                                                "left",
                                        }}
                                    >
                                        <FaClock />

                                        <span>
                                            This page checks
                                            automatically for
                                            verification
                                            updates. Please
                                            do not submit
                                            another payment.
                                        </span>
                                    </div>
                                </div>
                            </section>

                            <aside className="paf-card paf-side">
                                <SideHeader
                                    icon={
                                        <FaShieldAlt />
                                    }
                                    title="Verification"
                                    subtitle="Administration review"
                                />

                                <SummaryRow
                                    label="Status"
                                    value="Awaiting Verification"
                                />

                                <SummaryRow
                                    label="Application"
                                    value={
                                        applicationNumber
                                    }
                                />

                                <SummaryRow
                                    label="Applicant"
                                    value={
                                        applicantName
                                    }
                                />

                                <SummaryRow
                                    label="Program"
                                    value={safeText(
                                        application?.program
                                    )}
                                />

                                <div
                                    className="paf-security"
                                    style={{
                                        marginTop:
                                            14,
                                    }}
                                >
                                    <FaShieldAlt />

                                    <span>
                                        Once your payment is
                                        verified, your official
                                        entrance receipt will
                                        become available.
                                    </span>
                                </div>
                            </aside>
                        </motion.div>
                    )}

                    {isVerified && (
                        <motion.div
                            key="verified"
                            className="paf-layout"
                            initial={{
                                opacity: 0,
                                y: 8,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            exit={{
                                opacity: 0,
                            }}
                        >
                            <section className="paf-card">
                                <div className="paf-status">
                                    <div className="paf-status-icon verified">
                                        <FaCheckCircle />
                                    </div>

                                    <h2 className="paf-status-title verified">
                                        Payment Verified
                                    </h2>

                                    <p className="paf-status-text">
                                        Your entrance fee
                                        payment has been
                                        verified successfully.
                                        Your official receipt
                                        is now available.
                                    </p>

                                    <div className="paf-receipt-box">
                                        <div className="paf-receipt-heading">
                                            <FaReceipt />
                                            Official Entrance Fee Receipt
                                        </div>

                                        <p className="paf-receipt-text">
                                            Your payment has been verified successfully.
                                            You can now view your official receipt or
                                            proceed to the entrance examination.
                                        </p>

                                        <div
                                            style={{
                                                display:
                                                    "grid",
                                                gridTemplateColumns:
                                                    "repeat(2, minmax(0, 1fr))",
                                                gap: 10,
                                            }}
                                        >
                                            <button
                                                type="button"
                                                className="paf-receipt-button"
                                                onClick={
                                                    handleOpenReceipt
                                                }
                                            >
                                                <FaReceipt />
                                                View Official Receipt
                                                <FaArrowRight />
                                            </button>

                                            <button
                                                type="button"
                                                className="paf-receipt-button"
                                                onClick={
                                                    handleOpenEntranceSubjects
                                                }
                                                style={{
                                                    background:
                                                        `linear-gradient(135deg, ${BRAND.green}, #3D8F1C)`,
                                                }}
                                            >
                                                <FaCheckCircle />
                                                Start Entrance Examination
                                                <FaArrowRight />
                                            </button>
                                        </div>
                                    </div>

                                    <PaymentRecord
                                        payment={
                                            payment
                                        }
                                        amount={
                                            AMOUNT
                                        }
                                        currency={
                                            CURRENCY
                                        }
                                        verified
                                        applicantName={
                                            applicantName
                                        }
                                        applicationNumber={
                                            applicationNumber
                                        }
                                        program={safeText(
                                            application?.program
                                        )}
                                    />
                                </div>
                            </section>

                            <aside className="paf-card paf-side">
                                <SideHeader
                                    icon={
                                        <FaCheck />
                                    }
                                    title="Verification Complete"
                                    subtitle="Payment confirmed"
                                    success
                                />

                                <SummaryRow
                                    label="Status"
                                    value="VERIFIED"
                                    highlight
                                />

                                <SummaryRow
                                    label="Amount"
                                    value={formatMoney(
                                        payment?.amount ||
                                            AMOUNT,
                                        payment?.currency ||
                                            CURRENCY
                                    )}
                                    highlight
                                />

                                <SummaryRow
                                    label="Payment Date"
                                    value={formatDate(
                                        payment?.payment_date
                                    )}
                                />

                                <SummaryRow
                                    label="Verified Date"
                                    value={formatDate(
                                        payment?.verified_date ||
                                            payment?.verified_at
                                    )}
                                />

                                <button
                                    type="button"
                                    className="paf-receipt-button"
                                    style={{
                                        marginTop: 14,
                                    }}
                                    onClick={
                                        handleOpenReceipt
                                    }
                                >
                                    <FaReceipt />
                                    View Receipt
                                    <FaArrowRight />
                                </button>

                                <button
                                    type="button"
                                    className="paf-receipt-button"
                                    style={{
                                        marginTop: 9,
                                        background:
                                            `linear-gradient(135deg, ${BRAND.green}, #3D8F1C)`,
                                    }}
                                    onClick={
                                        handleOpenEntranceSubjects
                                    }
                                >
                                    <FaCheckCircle />
                                    Start Entrance Examination
                                    <FaArrowRight />
                                </button>
                            </aside>
                        </motion.div>
                    )}

                    {isRejected && (
                        <motion.section
                            key="rejected"
                            className="paf-card"
                            initial={{
                                opacity: 0,
                                y: 8,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            exit={{
                                opacity: 0,
                            }}
                        >
                            <div className="paf-status">
                                <div className="paf-status-icon rejected">
                                    <FaTimesCircle />
                                </div>

                                <h2 className="paf-status-title rejected">
                                    Payment Rejected
                                </h2>

                                <p className="paf-status-text">
                                    The submitted payment
                                    could not be verified by
                                    the administration.
                                </p>

                                {payment?.notes && (
                                    <div className="paf-rejected-note">
                                        <strong>
                                            Administration Note
                                        </strong>

                                        <div
                                            style={{
                                                marginTop:
                                                    5,
                                            }}
                                        >
                                            {safeText(
                                                payment.notes
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div
                                    className="paf-security"
                                    style={{
                                        maxWidth:
                                            600,
                                        margin:
                                            "18px auto 0",
                                        textAlign:
                                            "left",
                                    }}
                                >
                                    <FaExclamationTriangle
                                        style={{
                                            color:
                                                BRAND.red,
                                        }}
                                    />

                                    <span>
                                        Please contact the
                                        appropriate
                                        administration office
                                        if you believe this
                                        rejection was made in
                                        error.
                                    </span>
                                </div>
                            </div>
                        </motion.section>
                    )}
                </AnimatePresence>

                <footer className="paf-footer">
                    <FaLock />
                    Secure Entrance Payment Portal
                </footer>
            </div>
        </div>
    );
};

const StepIndicator = ({
    status,
}) => {
    const steps = [
        {
            number: 1,
            label: "Application",
        },
        {
            number: 2,
            label: "Payment",
        },
        {
            number: 3,
            label: "Verification",
        },
        {
            number: 4,
            label: "Receipt",
        },
    ];

    const activeStep =
        status === "not_submitted"
            ? 2
            : status === "submitted" ||
              status === "pending" ||
              status === "rejected"
            ? 3
            : status === "verified"
            ? 4
            : 1;

    return (
        <div className="paf-steps">
            {steps.map((step) => {
                const complete =
                    step.number <
                    activeStep;

                const active =
                    step.number ===
                    activeStep;

                return (
                    <div
                        key={
                            step.number
                        }
                        className={`paf-step ${
                            complete
                                ? "complete"
                                : active
                                ? "active"
                                : ""
                        }`}
                    >
                        <div className="paf-step-number">
                            {complete ? (
                                <FaCheck />
                            ) : (
                                step.number
                            )}
                        </div>

                        <span>
                            {step.label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const Field = ({
    label,
    children,
}) => (
    <div className="paf-field">
        <label className="paf-label">
            {label}
        </label>

        {children}
    </div>
);

const InfoItem = ({
    label,
    value,
}) => (
    <div className="paf-app-item">
        <span className="paf-label">
            {label}
        </span>

        <div className="paf-value">
            {safeText(value)}
        </div>
    </div>
);

const SummaryRow = ({
    label,
    value,
    highlight = false,
}) => (
    <div className="paf-row">
        <span className="paf-row-label">
            {label}
        </span>

        <strong
            className="paf-row-value"
            style={{
                color: highlight
                    ? BRAND.blue
                    : BRAND.navy,
            }}
        >
            {safeText(value)}
        </strong>
    </div>
);

const SideHeader = ({
    icon,
    title,
    subtitle,
    success = false,
}) => (
    <div className="paf-side-header">
        <div
            className="paf-side-icon"
            style={
                success
                    ? {
                          background:
                              "#EAF7E5",
                          color:
                              BRAND.green,
                      }
                    : undefined
            }
        >
            {icon}
        </div>

        <div>
            <h3 className="paf-side-title">
                {title}
            </h3>

            <div className="paf-side-subtitle">
                {subtitle}
            </div>
        </div>
    </div>
);

const PaymentRecord = ({
    payment,
    amount,
    currency,
    verified = false,
    applicantName,
    applicationNumber,
    program,
}) => (
    <div className="paf-record">
        <h3 className="paf-record-title">
            {verified
                ? "Verified Payment Details"
                : "Payment Details"}
        </h3>

        {applicantName && (
            <SummaryRow
                label="Applicant"
                value={
                    applicantName
                }
            />
        )}

        {applicationNumber && (
            <SummaryRow
                label="Application"
                value={
                    applicationNumber
                }
            />
        )}

        {program && (
            <SummaryRow
                label="Program"
                value={program}
            />
        )}

        <SummaryRow
            label="Amount"
            value={formatMoney(
                payment?.amount ||
                    amount,
                payment?.currency ||
                    currency
            )}
            highlight
        />

        <SummaryRow
            label="Payment Type"
            value={
                payment?.payment_type_display ||
                payment?.payment_type
            }
        />

        <SummaryRow
            label="Reference"
            value={
                payment?.transaction_reference
            }
        />

        <SummaryRow
            label="Payment Date"
            value={formatDate(
                payment?.payment_date
            )}
        />

        {verified && (
            <SummaryRow
                label="Verified Date"
                value={formatDate(
                    payment?.verified_date ||
                        payment?.verified_at
                )}
            />
        )}
    </div>
);

const Spinner = ({
    light = false,
}) => (
    <span
        style={{
            width: 15,
            height: 15,
            border: light
                ? "2px solid rgba(255,255,255,.35)"
                : "2px solid rgba(6,42,75,.2)",
            borderTopColor: light
                ? "#FFFFFF"
                : BRAND.blue,
            borderRadius: "50%",
            display: "inline-block",
            animation:
                "pafSpin .7s linear infinite",
            flexShrink: 0,
        }}
    />
);

export default EntranceRegistrationPage;